export type ChartRow = Record<string, unknown>;
export type ChartLineKey = { dataKey: string };

export type PreparedChartData = {
  data: ChartRow[];
  yDomain?: [number, number];
};

function isPositiveNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function padLogDomain(min: number, max: number): [number, number] {
  const paddedMin = min < 1 ? min * 0.75 : min * 0.85;
  const paddedMax = max * 1.15;
  return [Math.max(paddedMin, Number.MIN_VALUE), Math.max(paddedMax, 1)];
}

/**
 * Recharts log scales fail if any plotted value is 0 or negative.
 * Keep x-axis fields intact, but null non-positive series values in log mode.
 */
export function prepareChartDataForScale(
  rows: ChartRow[] | undefined,
  lines: ChartLineKey[],
  logScale: boolean,
): PreparedChartData {
  if (!rows?.length || !logScale || !lines.length) {
    return { data: rows ?? [] };
  }

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  const keys = lines.map((line) => line.dataKey);

  const sanitized = rows.map((row) => {
    const next: ChartRow = { ...row };
    for (const key of keys) {
      const value = row[key];
      if (isPositiveNumber(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      } else if (typeof value === "number") {
        next[key] = null;
      }
    }
    return next;
  });

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { data: sanitized };
  }

  return {
    data: sanitized,
    yDomain: padLogDomain(min, max),
  };
}
